import styled from 'styled-components';

import { colors, transition } from 'theme';

export type InputStatus = 'neutral' | 'success' | 'error';
export type InputStyleVariant = 'default' | 'contrast' | 'light-border';
export type InputPrefixVariant = 'default' | 'contrast' | 'transparent';

export const InputContainer = styled.div<{
  $status: InputStatus;
  $styleVariant: InputStyleVariant;
  $prefixVariant: InputPrefixVariant;
  $fullHeight: boolean;
  $fullWidth: boolean;
}>`
  position: relative;
  overflow: hidden;
  display: flex;

  font-size: 18px;
  height: ${({ $fullHeight }) => ($fullHeight ? '100%' : '45px')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  background: var(--input-background-color);

  border-radius: 8px;
  color: ${colors.darkContentGrey};
  transition: all ${transition};

  &::placeholder {
    color: ${colors.lightContentGrey};
  }

  &:focus-within {
    border-color: ${colors.brandBlue};
    box-shadow: 0 0 0 1px ${colors.brandBlue};
  }

  ${({ $status }) => {
    switch ($status) {
      case 'neutral':
        return `
            --input-highlight-color: var(--colors-elementGrey);
          `;
      case 'success':
        return `
            --input-highlight-color: green;
            box-shadow: 0 0 0 1px var(--input-highlight-color);
          `;
      case 'error':
        return `
            --input-highlight-color: red;
            box-shadow: 0 0 0 1px var(--input-highlight-color);
          `;
    }
  }}

  ${({ $prefixVariant }) => {
    switch ($prefixVariant) {
      case 'default':
        return `
            --input-affix-text-color: var(--color-lightContentGrey);
            --input-affix-background-color: var(--color-selectionGrey);
           
            &:focus-within {
              --input-affix-text-color: ${colors.white};
              --input-affix-background-color: ${colors.brandBlue};
            }
        `;
      case 'contrast':
        return `
            --input-affix-background-color: var(--color-inputGrey);
            --input-affix-text-color: ${colors.lightContentGrey};
          `;
      case 'transparent':
        return `
            --input-affix-text-color: var(--color-lightContentGrey);
            
            &:focus-within {
              --input-affix-text-color: ${colors.brandBlue};
            }
        `;
    }
  }}

  ${({ $styleVariant }) => {
    switch ($styleVariant) {
      case 'default':
        return `
            --input-background-color: var(--color-white);
            --input-highlight-color: var(--color-elementGrey);
            border: solid 1px var(--input-highlight-color);

            &:focus-within {
              box-shadow: none;
            }
          `;
      case 'contrast':
        return `
            --input-background-color: var(--color-inputGrey);
            --input-highlight-color: var(--color-elementGrey);
          `;
      case `light-border`:
        return `
            --input-background-color: var(--color-white);
            --input-highlight-color: var(--color-selectionGrey);
            border: solid 1px var(--input-highlight-color);

            &:focus-within {
              box-shadow: none;
            }
          `;
    }
  }}
`;

export const InputAffixLabel = styled.label`
  height: 100%;
  display: inline-flex;
  align-items: center;
  color: var(--input-affix-text-color);
  background: var(--input-affix-background-color);
  transition: all var(--transition);
`;

/* The input itself doesn't have many styles, most are applied to the container */
export const InputElement = styled.input`
  padding: 11px 12px;
  flex: 1;
  width: 100%;

  appearance: none;
  box-shadow: none;
  border: none;
  outline: none;
  font: inherit;
  background: transparent;
`;
